import React, { useState, useEffect, useReducer, useRef } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import isMobile from 'ismobilejs';
import { saveAs } from 'file-saver';

import DesktopLayout from './DesktopLayout';
import MobileLayout from './MobileLayout';

import UnsavedHeader from '../../../common/UnsavedHeader';
import ErrorBoundary from '../../../ErrorBoundary';

import {
  fetchGetFeatures,
  fetchGetAttributes,
  fetchGetAdditionalDescs,
  fetchGetItemById,
  fetchAddItem,
  fetchAddFeature,
  fetchAddAttribute,
  fetchAddImage,
  fetchDeleteVariant,
  fetchEditItem,
  fetchReorderImage,
  fetchDeleteImage,
  fetchArchiveItem,
  fetchRestoreItem,
  fetchHideShowItem,
  fetchCopyItem,
  fetchGetProductAppearance,
  fetchExportMovements,
  fetchGetSKUSets,
  fetchUpdateSKUSet,
  fetchGetEshops,
  fetchGetProductsHistory,
  fetchGetProductMargin,
  fetchStatisticsOfUsingChestnyznak,
} from '../../../../actions/items';

import { showModal } from '../../../../actions/modal';

import {
  checkCollectionsDiff,
  checkIfProductHasVariants,
  parseError,
  getInitialStock,
  getProductEncodedUrl,
} from '../../../../helpers';

import { VAT_RATES, PRODUCT_TYPES, PRICES_MOCK } from '../../../../config';
import Adaptive from '../../../common/Adaptive';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const itemsTypes = {
  SKU: 'Новый товар',
  BUNDLE: 'Новый виртуальный набор',
  PHYSICAL_SET: 'Новый комплект',
  SUBSCRIPTION: 'Новая подписка',
  SERVICE: 'Новая услуга',
  AD_ATTACHMENT: 'Новое доп. вложение',
  PACKING_MATERIAL_BOXES: 'Новый упаковочный короб',
  PACKING_MATERIAL_OTHER: 'Новый расходный материал',
  PACKING_MATERIAL_ATTACHMENT: 'Новое допвложение для упаковки',
  COMMON_PACKING_MATERIAL: 'Новый общий материал',
};

const itemsTypesTitles = {
  SKU: 'Товар',
  BUNDLE: 'Виртуальный набор',
  PHYSICAL_SET: 'Комплект',
  SUBSCRIPTION: 'Подписка',
  SERVICE: 'Услуга',
  AD_ATTACHMENT: 'Доп. вложение',
  PACKING_MATERIAL_BOXES: 'Упаковочный короб',
  PACKING_MATERIAL_OTHER: 'Расходный материал',
  PACKING_MATERIAL_ATTACHMENT: 'Допвложение для упаковки',
  COMMON_PACKING_MATERIAL: 'Общие материалы',
};

const generateVariants = (options, currentVariants = [], initialProps) => {
  const basicVariantId = currentVariants.length ? currentVariants[0].id : null;
  let shouldUseBasicVariantId = false;

  if (basicVariantId) {
    shouldUseBasicVariantId = true;
  }

  const f = (a, b) => [].concat(...a.map((d) => b.map((e) => [].concat(d, e))));
  const cartesian = (a, b, ...c) => (b ? cartesian(f(a, b), ...c) : a);
  const data = options
    .filter((option) => option.values && option.values.length)
    .map((option) => {
      return option.values.map((v) => {
        const obj = {
          code: option.code,
          stringValue: v.stringValue || null,
          decimalValue: v.decimalValue || null,
          isNew: Boolean(v.isNew),
        };
        if (option.isNewFeature) obj['isNewFeature'] = true;
        if (option.type) obj['type'] = option.type;
        return obj;
      });
    });
  const cartesianResult = data.length ? cartesian(...data) : [];

  const variants = cartesianResult.map((c, i) => {
    let elem = c;
    if (!Array.isArray(c)) elem = [c];

    const existVariant = currentVariants.find((v) =>
      checkCollectionsDiff(
        elem.map((o) => ({
          code: o.code,
          stringValue: o.stringValue,
          decimalValue: o.decimalValue,
        })),
        v.optionsUsed?.map((o) => ({
          code: o.code,
          stringValue: o.stringValue,
          decimalValue: o.decimalValue,
        })),
      ),
    );

    if (existVariant) {
      if (shouldUseBasicVariantId) {
        shouldUseBasicVariantId = false;
        return { ...existVariant, id: basicVariantId };
      }
      return existVariant;
    }

    const newVariant = {
      ...initialProps,
      optionsUsed: elem,
      isNew: true,
      temporaryId: uuidv4(),
    };

    if (shouldUseBasicVariantId) {
      shouldUseBasicVariantId = false;
      newVariant.id = basicVariantId;
    }

    return newVariant;
  });
  return variants;
};

const filterVariants = (options, variants, lastAction, handleAddVariantsForRemoving) => {
  if (!lastAction) return variants.slice();
  let newVariants = [];
  switch (lastAction.type) {
    case 'addTag':
      newVariants = variants.map((v) => {
        return {
          ...v,
          optionsUsed: [...v.optionsUsed, lastAction.data.newOption],
          forSaving: true,
        };
      });
      break;
    case 'removeTag':
      const removingObj = {};
      newVariants = variants.filter((v) => {
        let isFiltered = true;
        if (v.optionsUsed) {
          v.optionsUsed.forEach((o) => {
            if (
              o.code === lastAction.data.tag.code &&
              o.stringValue === lastAction.data.tag.stringValue &&
              o.decimalValue === lastAction.data.tag.decimalValue
            ) {
              removingObj[v.id] = v;
              isFiltered = false;
            }
          });
        }
        if (Object.keys(removingObj).length) {
          handleAddVariantsForRemoving(removingObj);
        }
        return isFiltered;
      });
      break;
    case 'removeOption':
      newVariants = variants.map((v) => {
        return {
          ...v,
          optionsUsed: v.optionsUsed.filter((o) => o.code !== lastAction.data.option.code),
          forSaving: true,
        };
      });
      break;
    default:
      newVariants = variants.slice();
      break;
  }

  return newVariants;
};

const validationSchema = Yup.object().shape({
  width: Yup.number().integer('Габариты указываются целыми числами'),
  height: Yup.number().integer('Габариты указываются целыми числами'),
  depth: Yup.number().integer('Габариты указываются целыми числами'),
});

const getFormikInitialValues = (props) => {
  const { warehouses, type } = props;

  return {
    shortName: itemsTypes[type || 'SKU'],
    weight: 1,
    weightType: 'kg',
    width: 10,
    height: 10,
    depth: 10,
    vat: VAT_RATES[0].value,
    type: type || PRODUCT_TYPES[0].value,
    barcodes: [{ value: '', isDefault: true, type: 'COMMON' }],
    stock: getInitialStock(warehouses),
    updateStockTotal: false,
    prices: PRICES_MOCK,
    autoPriceCalculation: false,
    discountPercent: 0,
    discountFix: 0,
    autoDimensionsCalculation: false,
    eshopSettings: null,
    useBatchAccounting: false,
    expirationMode: 'NO_EXPIRATION',
    expirationDaysLimit: null,
    accountingAttributes: [],
  };
};

const getInitialState = (props) => {
  const isNew = props.match.params.id === 'new';
  return {
    isArchived: props.history.location.search.indexOf('?archived') !== -1,
    loading: false,
    deleteLoading: false,
    isNew,
    isSavedVariants: false,
    productId: !isNew ? props.match.params.id : null,
    features: [],
    featuresDidLoaded: false,
    attributes: [],
    accountingAttributes: [],
    options: [],
    variants: [],
    removingVariants: {},
    useVariants: false,
    needSaving: false,
    needToGenerateVariants: false,
    selectedVariants: [],
    allSelectedVariants: false,
    lastAction: null,
    variantsValidation: true,
    appearanceFields: [],
    skuSet: null,
    unSavedSkuSet: null,
    type: props.history.location?.state?.type || 'SKU',
    eshops: [],
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'setArchived':
      return { ...state, isArchived: action.isArchived };
    case 'setFeatures':
      return { ...state, features: action.features, featuresDidLoaded: true };
    case 'setAttributes':
      return { ...state, attributes: action.attributes };
    case 'setAdditionalDescs':
      return { ...state, additionalDescsOptions: action.additionalDescs };
    case 'setOptions':
      return { ...state, options: action.options };
    case 'setVariants':
      return { ...state, variants: action.variants };
    case 'setSelectedVariants':
      return { ...state, selectedVariants: action.selectedVariants };
    case 'setAllSelectedVariants':
      return { ...state, allSelectedVariants: action.allSelectedVariants };
    case 'updateRemovingVariants':
      return { ...state, removingVariants: action.removingVariants };
    case 'setLoading':
      return { ...state, loading: action.loading };
    case 'setDeleteLoading':
      return { ...state, deleteLoading: action.deleteLoading };
    case 'updateProduct':
      return {
        ...state,
        productId: action.product.id,
        currentProduct: action.product,
        isNew: false,
      };
    case 'updateVariantsUsing':
      return { ...state, useVariants: action.useVariants };
    case 'updateGenerateVariantsStatus':
      return { ...state, needToGenerateVariants: action.needToGenerateVariants };
    case 'updateSavedStatus':
      return { ...state, isSavedVariants: action.isSavedVariants };
    case 'setNeedSavingStatus':
      return { ...state, needSaving: action.needSaving };
    case 'setLastAction':
      return { ...state, lastAction: action.lastAction };
    case 'updateVariantsValidation':
      return { ...state, variantsValidation: action.validation };
    case 'setAppearanceFields':
      return { ...state, appearanceFields: action.appearanceFields };
    case 'setSkuSet':
      return { ...state, skuSet: action.skuSet };
    case 'setUnSavedSkuSetSkuSet':
      return { ...state, unSavedSkuSet: action.unSavedSkuSet };
    case 'setType':
      return { ...state, physicalSet: action.physicalSet };
    case 'setEshops':
      return { ...state, eshops: action.eshops };
    default:
      return state;
  }
};

const mapVariants = (variants) => {
  return variants.map((v) => ({ ...v, weight: v.weight / 1000 }));
};

const getVariantsValidation = async (variants) => {
  let valid = true;
  await Yup.array()
    .of(
      Yup.object().shape({
        // price: Yup.number()
        //     .typeError()
        //     .moreThan(0)
        //     .nullable()
      }),
    )
    .validate(variants)
    .catch((error) => {
      valid = error.path;
    });

  if (valid === true) {
    return valid;
  }

  const splitted = valid.split('.');
  return { index: Number(splitted[0].split('[')[1].split(']')[0]), prop: splitted[1] };
};

const SingleItemPage = (props) => {
  const {
    history,
    fetchGetFeatures,
    fetchGetAttributes,
    fetchGetAdditionalDescs,
    fetchGetItemById,
    fetchAddItem,
    fetchAddFeature,
    fetchAddAttribute,
    fetchAddImage,
    fetchDeleteVariant,
    fetchEditItem,
    fetchReorderImage,
    fetchDeleteImage,
    fetchArchiveItem,
    fetchRestoreItem,
    fetchHideShowItem,
    fetchCopyItem,
    fetchGetProductAppearance,
    fetchUpdateSKUSet,
    fetchGetSKUSets,
    showModal,
    setCustomHeader,
    fetchGetEshops,
  } = props;

  const reduxDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, getInitialState(props));
  const [unsaved, setUnsavedStatus] = useState(false);
  const [ordered, setOrderStatus] = useState(false);
  const [oldVariants, setOldVariants] = useState([]);
  const warehouses = useSelector((state) => state.orders.warehouses);
  const productsPriceMarginHistory = useSelector(
    (state) => state?.items?.productsPriceMarginHistory?.history,
  );
  const productMarginShipped = useSelector((state) => state.items.productMarginShipped);
  const productMarginDelivered = useSelector((state) => state.items.productMarginDelivered);

  const monoboxesUpdateRef = useRef();

  const {
    isNew,
    isSavedVariants, //пропса для правильной работы редактора опций и вариантов
    isArchived,
    productId,
    features,
    featuresDidLoaded,
    attributes,
    additionalDescsOptions,
    options,
    variants,
    selectedVariants,
    allSelectedVariants,
    currentProduct,
    loading,
    deleteLoading,
    useVariants,
    needSaving,
    needToGenerateVariants,
    removingVariants,
    lastAction,
    variantsValidation,
    appearanceFields,
    skuSet,
    type,
    isVirtualSet,
    unSavedSkuSet,
    eshops,
  } = state;

  const { t } = useAutoTranslation();

  const formik = useFormik({
    initialValues: getFormikInitialValues({ warehouses, type }),
    onSubmit: async (values, bag) => {
      if (monoboxesUpdateRef.current?.updateMonoboxes) {
        const product = await monoboxesUpdateRef.current.updateMonoboxes();
        successfulUpdateDataHandler(product);
        return;
      }

      if (isNew) {
        return saveProduct();
      } else {
        return editProduct();
      }
    },
    validationSchema,
  });
  const { values, errors, submitCount, handleChange, handleSubmit, setFieldValue, isValid } =
    formik;
  const allIsValid = isValid && variantsValidation === true;

  const images =
    currentProduct && currentProduct.images
      ? currentProduct.images.sort((a, b) => Number(a.pos) - Number(b.pos))
      : [];

  useEffect(() => {
    (async function () {
      const promisesArr = [
        fetchGetFeatures(),
        fetchGetAttributes(),
        fetchGetAdditionalDescs(),
        fetchGetEshops(),
      ];
      if (!isNew) {
        promisesArr.push(fetchGetProductAppearance(productId));
      }
      const result = await Promise.all(promisesArr);

      const features = result[0] || [];
      const attributes = result[1] || [];
      const additionalDescs = result[2] || [];
      const eshops = result[3].eshops || [];

      dispatch({ type: 'setFeatures', features: features.filter((f) => f.title) });
      dispatch({ type: 'setAttributes', attributes });
      dispatch({ type: 'setAdditionalDescs', additionalDescs });
      dispatch({ type: 'setEshops', eshops });

      if (!isNew) {
        const appearanceFields = result[4];
        dispatch({ type: 'setAppearanceFields', appearanceFields });
      }
    })();
  }, []);

  useEffect(() => {
    (async function () {
      const validation = await getVariantsValidation(variants);
      dispatch({ type: 'updateVariantsValidation', validation });
    })();
  }, [variants]);

  useEffect(() => {
    if (currentProduct?.type) {
      setFieldValue('type', currentProduct.type);
    }
  }, [currentProduct?.type]);

  useEffect(() => {
    setCustomHeader(
      unsaved ? (
        <UnsavedHeader handleSubmit={handleSubmit} loading={loading} disabled={!allIsValid} />
      ) : null,
    );
    return () => {
      setCustomHeader(null);
    };
  }, [unsaved, loading, allIsValid]);

  const refreshProduct = async (id) => {
    dispatch({ type: 'setLoading', loading: 'main' });

    const currentId = productId ? productId : id;
    const product = await fetchGetItemById(currentId, isArchived).finally(() => {
      dispatch({ type: 'setLoading', loading: false });
    });

    dispatch({ type: 'updateProduct', product });
  };

  const actualizeSkuSet = (product = currentProduct) => {
    return (async function () {
      const newData = await fetchGetSKUSets({ id: product?.variants[0].id });

      await dispatch({ type: 'setSkuSet', skuSet: newData.skuSets });
      await refreshProduct();
    })();
  };

  const handleSortBarcodes = (barcodes) => {
    if (!barcodes) return;

    return barcodes.sort(function (a, b) {
      if (a.type > b.type) {
        return 1;
      }
      if (a.type < b.type) {
        return -1;
      }
      return 0;
    });
  };

  useEffect(() => {
    if (featuresDidLoaded && productId) {
      (async function () {
        const product = await fetchGetItemById(productId, isArchived);

        dispatch({ type: 'updateProduct', product });

        const hasVariants = checkIfProductHasVariants(product);

        dispatch({ type: 'updateVariantsUsing', useVariants: hasVariants });

        const newFormikValues = {
          shortName: product.shortName,
          description: product.description,
          prices:
            !hasVariants &&
            product.variants &&
            product.variants.length &&
            product.variants[0].prices
              ? product.variants[0].prices.reduce((a, c) => {
                  a[c.priceCode] = c;
                  return a;
                }, {})
              : PRICES_MOCK,
          weight:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].weight ?? null
              : 0,
          width:
            !hasVariants &&
            product.variants &&
            product.variants.length &&
            product.variants[0].dimensions
              ? product.variants[0].dimensions.width ?? 0
              : 0,
          height:
            !hasVariants &&
            product.variants &&
            product.variants.length &&
            product.variants[0].dimensions
              ? product.variants[0].dimensions.height ?? 0
              : 0,
          depth:
            !hasVariants &&
            product.variants &&
            product.variants.length &&
            product.variants[0].dimensions
              ? product.variants[0].dimensions.depth ?? 0
              : 0,
          article:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].article
              : '',
          barcodes:
            !hasVariants &&
            product.variants &&
            product.variants.length &&
            product.variants[0].barcodes
              ? handleSortBarcodes(product.variants[0].barcodes)
              : [{ value: '', isDefault: true, type: 'COMMON' }],
          extId:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].extId
              : '',
          extId2:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].extId2
              : '',
          extId3:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].extId3
              : '',
          extId4:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].extId4
              : '',
          extId5:
            !hasVariants && product.variants && product.variants.length
              ? product.variants[0].extId5
              : '',
          stock:
            !hasVariants && product.variants && product.variants.length && product.variants[0].stock
              ? getInitialStock(warehouses, product.variants[0].stock)
              : getInitialStock(warehouses),
          updateStockTotal: false,
          brandName: product.brandName,
          vat: product.vat,
          type: product.type,
          countryOfOrigin: product.countryOfOrigin,
          isBestseller: product.isBestseller,
          isNovelty: product.isNovelty,
          boxNeeded: product.boxNeeded,
          fragile: product.fragile,
          attributes: product.attributes,
          accountingAttributes: product.accountingAttributes || [],
          additionalDescriptions: product.additionalDescriptions,
          tnvedCode: product.tnvedCode,
          internationalDescription: product.internationalDescription,
          discountPercent: (product.variants && product.variants[0]?.discountPercent) || 0,
          discountFix: product.variants[0].discountFix || 0,
          autoPriceCalculation: product.variants && product.variants[0]?.autoPriceCalculation,
          autoDimensionsCalculation:
            product.variants && product.variants[0]?.autoDimensionsCalculation,
          eshopSettings: product.variants?.[0]?.eshopSettings || [],
          useBatchAccounting: product.useBatchAccounting,
          expirationDaysLimit: product.expirationDaysLimit?.toString(),
          expirationMode: product.expirationMode ?? 'NO_EXPIRATION',
          noAvia: product.noAvia,
          expirationMonthsLimit: product.expirationMonthsLimit,
          composition: product.composition,
          manufacturedBy: product.manufacturedBy,
          warranty: product.warranty,
          warehouseCategoryCode: product.warehouseCategoryCode,
          warehouseCategoryTitle: product.warehouseCategoryTitle,
          eacCertificationType: product.eacCertificationType || '',
          stickerEAC: product.stickerEAC || false,
          supplier: product.supplier || '',

          otherCost: product.variants?.[0]?.marginParams?.otherCost,
          fulfillmentCost: product.variants?.[0]?.marginParams?.fulfillmentCost,
          marketplaceCosts: product.variants?.[0]?.marginParams?.marketplaceCosts,
          allowSkipCis: product.allowSkipCis,
          gost: product.gost,
        };

        if (newFormikValues.weight < 500) {
          newFormikValues.weightType = 'gr';
        } else {
          newFormikValues.weightType = 'kg';
          newFormikValues.weight = newFormikValues.weight / 1000;
        }

        formik.setValues(newFormikValues);

        if (hasVariants) {
          //Если у продукта есть варианты - формируем массив существующих вариантов

          dispatch({ type: 'updateSavedStatus', isSavedVariants: true });

          const defaultOptions = {};
          product.variants.forEach((v) => {
            if (v.optionsUsed) {
              v.optionsUsed.forEach((o) => {
                if (!defaultOptions[o.code]) {
                  // если в массиве опций не существует опции - добавляем
                  defaultOptions[o.code] = {
                    code: o.code,
                    title: o.title,
                    values: [
                      {
                        stringValue: o.stringValue,
                        decimalValue: o.decimalValue,
                      },
                    ],
                  };
                } else {
                  if (
                    !defaultOptions[o.code].values.find((val) => {
                      const currentFeature = features.find((f) => f.code === o.code);
                      if (currentFeature) {
                        if (currentFeature.type === 'string') {
                          return val.stringValue === o.stringValue;
                        }
                        if (currentFeature.type === 'decimal') {
                          return val.decimalValue === o.decimalValue;
                        }
                      }
                      return false;
                    })
                  ) {
                    // если у опции нет текущего значения - добавляем
                    defaultOptions[o.code].values.push({
                      stringValue: o.stringValue,
                      decimalValue: o.decimalValue,
                    });
                  }
                }
              });
            }
          });

          dispatch({ type: 'setOptions', options: Object.values(defaultOptions) });

          let oldVariants = mapVariants(product.variants).sort((a, b) => {
            return a.ordering - b.ordering;
          });
          let newOldVariants = oldVariants.map((v, i) => {
            return {
              ...v,
              ordering: i,
            };
          });
          dispatch({ type: 'setVariants', variants: newOldVariants });
          setOldVariants(newOldVariants);
        } else {
          // Если у продукта нет вариантов, то проверяем состав комплектов
          if (
            product?.variants[0]?.type === 'PHYSICAL_SET' ||
            product?.variants[0]?.type === 'BUNDLE'
          ) {
            actualizeSkuSet(product);
          }
        }
      })();
    }
  }, [featuresDidLoaded, history.location.pathname]);

  useEffect(() => {
    if (needToGenerateVariants && options.length) {
      let newVariants = variants;
      if (!isSavedVariants) {
        if (!variants.length) {
          //Если первый вариант - переносим основной вариант
          newVariants = [
            {
              ...currentProduct.variants[0],
              weight: currentProduct.variants[0].weight / 1000,
              optionsUsed: [
                {
                  code: options[0].code,
                  type: options[0].type,
                  decimalValue: options[0].values[0]?.decimalValue,
                  stringValue: options[0].values[0]?.stringValue,
                  isNew: true,
                },
              ],
              barcodes: currentProduct.variants[0].barcodes?.slice() || [],
              dimensions: Object.assign({}, currentProduct.variants[0].dimensions),
              isNew: true,
            },
          ];
        } else {
          newVariants = generateVariants(options, variants, {
            id: null,
            weight: variants[0].weight,
            prices: variants[0].prices,
            oldPrice: variants[0].oldPrice,
            article: variants[0].article,
            dimensions: Object.assign({}, variants[0].dimensions),
          });
        }
      } else {
        newVariants = filterVariants(options, variants, lastAction, handleAddVariantsForRemoving);
      }
      dispatch({ type: 'updateGenerateVariantsStatus', needToGenerateVariants: false });
      dispatch({ type: 'setVariants', variants: newVariants });
    }
  }, [needToGenerateVariants]);

  useEffect(() => {
    if (isNew) {
      /*      setFieldValue('shortName', itemsTypes[values.type]);*/
      setFieldValue('autoDimensionsCalculation', false);
      setFieldValue('autoPriceCalculation', false);
    }
  }, [values.type]);

  useEffect(() => {
    if (unSavedSkuSet) {
      handleUpdateSkuSet();
    }
  }, [isNew]);

  const handleGetStatisticsOfUsingChestnyznak = async (arrOfVariantIds) => {
    await reduxDispatch(fetchStatisticsOfUsingChestnyznak(arrOfVariantIds));
  };

  useEffect(() => {
    if (currentProduct) {
      const arrOfVariantIds = currentProduct.variants.map((variant) => variant.id);
      handleGetStatisticsOfUsingChestnyznak(arrOfVariantIds);
    }
  }, [currentProduct]);

  const saveProduct = async () => {
    const {
      shortName,
      description,
      prices,
      weight,
      weightType,
      width,
      height,
      depth,
      article,
      brandName,
      extId,
      extId2,
      extId3,
      extId4,
      extId5,
      stock,
      updateStockTotal,
      vat,
      barcodes,
      type,
      attributes,
      accountingAttributes,
      additionalDescriptions,
      tnvedCode,
      internationalDescription,
      countryOfOrigin,
      isBestseller,
      isNovelty,
      boxNeeded,
      fragile,
      autoPriceCalculation,
      discountPercent,
      discountFix,
      autoDimensionsCalculation,
      eshopSettings,
      useBatchAccounting,
      expirationDaysLimit,
      expirationMode,
      noAvia,
      expirationMonthsLimit,
      composition,
      manufacturedBy,
      warranty,
      allowSkipCis,
      eacCertificationType,
      stickerEAC,
      supplier,
    } = values;

    if (type === 'PHYSICAL_SET' && !skuSet && !unSavedSkuSet) {
      toast.error(t('Необходимо добавить хотя бы один товар в комплект'));
      return;
    }
    if (type === 'BUNDLE' && !skuSet && !unSavedSkuSet) {
      toast.error(t('Необходимо добавить хотя бы один товар в виртуальный набор'));
      return;
    }

    const newProduct = {
      shortName,
      description,
      available: true,
      brandName,
      vat,
      type,
      attributes:
        attributes && attributes.length
          ? attributes
              .filter((a) => a.code && (a.stringValue || a.decimalValue))
              .map((a) => ({
                code: a.code,
                stringValue: a.stringValue,
                decimalValue: a.decimalValue,
              }))
          : null,
      accountingAttributes:
        accountingAttributes && accountingAttributes.length
          ? accountingAttributes.filter((a) => a.code && a.stringValue)
          : null,
      additionalDescriptions,
      tnvedCode,
      internationalDescription,
      countryOfOrigin,
      isBestseller,
      isNovelty,
      boxNeeded,
      fragile,
      useBatchAccounting,
      expirationDaysLimit: expirationDaysLimit === null ? null : Math.abs(+expirationDaysLimit),
      expirationMode,
      noAvia,
      expirationMonthsLimit,
      composition,
      manufacturedBy,
      warranty,
      allowSkipCis,
      eacCertificationType,
      stickerEAC,
      supplier,
    };

    const singleVariant = {
      prices:
        type !== 'AD_ATTACHMENT'
          ? Object.keys(prices).reduce((a, key) => {
              a.push({
                ...prices[key],
                value: Number(prices[key].value),
              });
              return a;
            }, [])
          : undefined,
      weight: weightType === 'gr' ? Number(weight) : Number(weight * 1000),
      dimensions: {
        width: Number(width),
        height: Number(height),
        depth: Number(depth),
      },
      article,
      brandName,
      barcodes: barcodes.filter((barcode) => barcode.value),
      extId,
      extId2,
      stock,
      updateStockTotal,
      autoPriceCalculation,
      discountPercent,
      discountFix,
      autoDimensionsCalculation,
      eshopSettings: eshopSettings && eshopSettings.length ? eshopSettings : [],
    };

    dispatch({ type: 'setLoading', loading: 'main' });

    return fetchAddItem({ newProduct, singleVariant })
      .then(({ result: product, variantErrors }) => {
        toast.info(`${itemsTypesTitles[values.type]} ${t('успешно сохранен')}`);
        dispatch({ type: 'updateProduct', product });
        history.push(getProductEncodedUrl(product.id));

        if (variantErrors) {
          toast.error(parseError(variantErrors));
        }
        return product;
      })
      .then(async (product) => {
        if (type === 'PHYSICAL_SET') {
          await handleUpdateSkuSet();
        }
        return product;
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        setUnsavedStatus(false);
        dispatch({ type: 'setAllSelectedVariants', allSelectedVariants: false });
        dispatch({ type: 'setSelectedVariants', selectedVariants: [] });
        dispatch({ type: 'setLoading', loading: false });
      });
  };

  useEffect(() => {
    if (currentProduct?.variants?.[0]) {
      const { prices, dimensions, weight, type } = currentProduct?.variants[0];
      if (prices) {
        setFieldValue(
          'prices',
          prices.reduce((a, c) => {
            a[c.priceCode] = c;
            return a;
          }, {}),
        );
      }
      if (weight < 500) {
        setFieldValue('weightType', 'gr');
        setFieldValue('weight', weight);
      } else {
        setFieldValue('weightType', 'kg');
        setFieldValue('weight', weight / 1000);
      }
      if (type !== 'BUNDLE') {
        setFieldValue('autoPriceCalculation', false);
        setFieldValue('autoDimensionsCalculation', false);
      }
      setFieldValue('width', dimensions?.width);
      setFieldValue('height', dimensions?.height);
      setFieldValue('depth', dimensions?.depth);
    }
    if (currentProduct) {
      setFieldValue('eshopSettings', currentProduct?.variants?.[0].eshopSettings);
    }
  }, [currentProduct]);

  const editProduct = async (newType) => {
    const {
      shortName,
      description,
      prices,
      weight,
      weightType,
      width,
      height,
      depth,
      article,
      brandName,
      extId,
      extId2,
      extId3,
      extId4,
      extId5,
      stock,
      updateStockTotal,
      vat,
      barcodes,
      type,
      attributes,
      accountingAttributes,
      additionalDescriptions,
      tnvedCode,
      internationalDescription,
      countryOfOrigin,
      isBestseller,
      isNovelty,
      boxNeeded,
      fragile,
      autoPriceCalculation,
      discountPercent,
      discountFix,
      autoDimensionsCalculation,
      eshopSettings,
      useBatchAccounting,
      expirationDaysLimit,
      expirationMode,
      noAvia,
      expirationMonthsLimit,
      composition,
      manufacturedBy,
      warranty,
      warehouseCategoryCode,
      fulfillmentCost,
      otherCost,
      marketplaceCosts,
      allowSkipCis,
      eacCertificationType,
      stickerEAC,
      supplier,
      gost,
    } = values;

    const getMarketplaceCosts = () => {
      if (marketplaceCosts) {
        const updatedCosts = marketplaceCosts.map((item) =>
          item.costPercent === '' ? { ...item, costPercent: 0 } : item,
        );
        return updatedCosts;
      } else {
        return [];
      }
    };

    const product = {
      ...currentProduct,
      shortName,
      description,
      brandName,
      vat,
      type: newType ? newType : type,
      attributes:
        attributes && attributes.length
          ? attributes
              .filter((a) => a.code && (a.stringValue || a.decimalValue))
              .map((a) => ({
                code: a.code,
                stringValue: a.stringValue,
                decimalValue: a.decimalValue,
              }))
          : null,
      accountingAttributes:
        accountingAttributes && accountingAttributes.length
          ? accountingAttributes.filter((a) => a.code && a.stringValue)
          : null,
      additionalDescriptions,
      tnvedCode,
      internationalDescription,
      countryOfOrigin,
      isBestseller,
      isNovelty,
      boxNeeded,
      fragile,
      useBatchAccounting,
      expirationDaysLimit: expirationDaysLimit === null ? null : Math.abs(+expirationDaysLimit),
      expirationMode,
      noAvia,
      expirationMonthsLimit,
      composition,
      manufacturedBy,
      warranty,
      warehouseCategoryCode,
      allowSkipCis,
      eacCertificationType,
      stickerEAC,
      supplier,
      gost,
    };

    if (product.collections) delete product.collections; //TODO Remove

    const discountType = discountFix ? 'FIX' : 'PERCENT';
    const discountValue = discountFix ? discountFix : discountPercent;

    const singleVariant = {
      prices:
        type !== 'AD_ATTACHMENT'
          ? Object.keys(prices).reduce((a, key) => {
              a.push({
                ...prices[key],
                value: Number(prices[key].value),
              });
              return a;
            }, [])
          : undefined,
      weight: weightType === 'gr' ? Number(weight) : Number(weight * 1000),
      dimensions: {
        width: Number(width),
        height: Number(height),
        depth: Number(depth),
      },
      article,
      brandName,
      extId,
      extId2,
      extId3,
      extId4,
      extId5,
      barcodes: barcodes ? barcodes.filter((b) => b.value) : barcodes,
      stock,
      updateStockTotal,
      autoPriceCalculation,
      discountValue: discountValue,
      discountType: discountType,
      autoDimensionsCalculation,
      eshopSettings: eshopSettings && eshopSettings.length ? eshopSettings : [],
      marginParams: {
        fulfillmentCost,
        otherCost,
        marketplaceCosts: getMarketplaceCosts(),
      },
    };

    dispatch({ type: 'setLoading', loading: 'main' });

    for (let id in removingVariants) {
      // удаляем ненужные варианты
      await fetchDeleteVariant(productId, id).catch((err) => {
        toast.error(parseError(err));
        dispatch({ type: 'setLoading', loading: false });
        throw new Error();
      });
    }

    const newFeatures = options.filter((o) => o.isNewFeature);
    for (let i in newFeatures) {
      await addNewFeature(newFeatures[i]);
    }

    product.variantsToUpdate = useVariants
      ? variants
          .filter((v) => v.isNew || v.forSaving || v.unsaved)
          .map((v) => (v.weight ? { ...v, weight: v.weight * 1000 } : v))
          .map((v) =>
            v.forSaving || v.unsaved || v.isNew ? { ...v, variantId: v.id, productId } : v,
          )
      : [{ ...singleVariant, productId, variantId: product.variants[0].id }];

    if (!options.length && variants.length === 1) {
      product.variantsToUpdate = [
        {
          ...currentProduct.variants[0],
          variantId: currentProduct.variants[0].id,
          productId,
          optionsUsed: null,
        },
      ];
    }

    if (ordered) {
      let newVariants = variants.sort((a, b) => {
        return Number(a.ordering) - Number(b.ordering);
      });

      newVariants.forEach((v, i) => {
        if (oldVariants[i].id != v.id) {
          product.variantsToUpdate.push({
            ...v,
            variantId: v.id,
            productId,
          });
        }
      });
    }
    fetchEditItem({ product })
      .then(async (product) => {
        const result = successfulUpdateDataHandler(product);
        return result;
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        dispatch({ type: 'setAllSelectedVariants', allSelectedVariants: false });
        dispatch({ type: 'setSelectedVariants', selectedVariants: [] });
        dispatch({ type: 'setLoading', loading: false });
      });
  };

  const successfulUpdateDataHandler = (product) => {
    toast.info(`${itemsTypesTitles[values.type]} ${t('успешно сохранен')}`);
    dispatch({ type: 'updateProduct', product });
    const hasVariants = checkIfProductHasVariants(product);

    if (hasVariants) {
      let oldVariants = mapVariants(product.variants).sort((a, b) => {
        return a.ordering - b.ordering;
      });
      let newOldVariants = oldVariants.map((v, i) => {
        return {
          ...v,
          ordering: i,
        };
      });

      dispatch({ type: 'setVariants', variants: newOldVariants });
      setOldVariants(newOldVariants);
      dispatch({ type: 'updateSavedStatus', isSavedVariants: true });
    } else {
      dispatch({ type: 'updateSavedStatus', isSavedVariants: false });
    }
    dispatch({ type: 'setNeedSavingStatus', needSaving: false });
    dispatch({ type: 'setLastAction', lastAction: null });
    dispatch({ type: 'updateRemovingVariants', removingVariants: {} });
    setUnsavedStatus(false);

    if (product.variantsResult && product.variantsResult[0]?.success === false) {
      toast.error(product.variantsResult[0].errors[0]?.message);
    }

    return product;
  };

  const addNewFeature = (feature) => {
    const newFeature = Object.assign({}, feature);
    newFeature.values = null;
    return fetchAddFeature(newFeature).then((result) => {
      dispatch({ type: 'setFeatures', features: [...features, newFeature] });
      toast.info(`${t('Свойство')} "${result.code}" ${t('успешно добавлено')}`);
    });
  };

  const addNewAttribute = (attribute) => {
    const newAttribute = Object.assign({}, attribute);
    newAttribute.values = [];
    return fetchAddAttribute(newAttribute).then((result) => {
      dispatch({ type: 'setAttributes', attributes: [...attributes, newAttribute] });
      toast.info(`${t('Атрибут')} "${result.code}" ${t('успешно добавлен')}`);
    });
  };

  const updateVariant = (value, variant, prop) => {
    let data = Object.assign({}, variant, { unsaved: true });
    switch (prop) {
      case 'width':
      case 'height':
      case 'depth':
        if (!data.dimensions) {
          const formikInitialValues = getFormikInitialValues(warehouses);
          data.dimensions = {
            width: values.width ? values.width : formikInitialValues.width,
            height: values.height ? values.height : formikInitialValues.height,
            depth: values.depth ? values.depth : formikInitialValues.depth,
          };
        }
        data.dimensions[prop] = Number(value);
        break;

      case 'barcodes':
        if (data.barcodes) {
          const findDefaultIndex = data.barcodes.findIndex((b) => b.isDefault);
          if (findDefaultIndex !== -1) {
            data[prop][findDefaultIndex] = { ...data[prop][findDefaultIndex], value };
          } else {
            data[prop][0] = { ...data[prop][0], value };
          }
        } else {
          data[prop] = [{ value, isDefault: true }];
        }
        break;

      case 'stockAvailable':
        data[prop] = value;
        data.updateStockAvailable = true;
        break;

      default:
        data[prop] = value;
    }

    const newVariants = variants.map((v) => {
      if (v.temporaryId ? v.temporaryId === variant.temporaryId : v.id === variant.id) {
        return data;
      }
      return v;
    });

    dispatch({ type: 'setVariants', variants: newVariants });
    setUnsavedStatus(true);
  };

  const moveVariant = (variants) => {
    setOrderStatus(true);
    const newVariants = variants.map((v, i) => {
      if (v.ordering == i) {
        return v;
      } else {
        return {
          ...v,
          ordering: i,
        };
      }
    });
    dispatch({ type: 'setVariants', variants: newVariants });
    setUnsavedStatus(true);
  };

  const updateVariantsState = (variant, variantIndex) => {
    if (variantIndex === 0 && variants.length > 1) {
      variants[1].id = variants[0].id;
    }

    const newVariants = variants.slice();
    newVariants.splice(variantIndex, 1);
    if (!newVariants.length) {
      dispatch({
        type: 'updateProduct',
        product: {
          ...currentProduct,
          variants: [
            {
              ...variant,
              optionsUsed: null,
              weight: variant.weight * 1000,
              isNew: false,
            },
          ],
        },
      });
    }
    dispatch({ type: 'setVariants', variants: newVariants });
  };

  const deleteVariant = async (variant) => {
    dispatch({ type: 'setDeleteLoading', deleteLoading: true });
    const variantIndex = variants.findIndex((v) =>
      !variant.isNew ? v.id === variant.id : v.temporaryId === variant.temporaryId,
    );
    if (!variant.isNew) {
      try {
        await fetchDeleteVariant(productId, variant.id);
        updateVariantsState(variant, variantIndex);

        toast.info(`${t('Вариант')} (id: ${variant.id}) ${t('успешно удален')}`);
      } catch (err) {
        toast.error(parseError(err));
      }
    } else {
      updateVariantsState(variant, variantIndex);
    }
    dispatch({ type: 'setDeleteLoading', deleteLoading: false });
  };

  const handleVariantsUsing = async (e) => {
    const isChecked = e.target.checked;
    if (!productId) {
      await saveProduct();
    } else {
      if (isChecked) {
        await editProduct();
      } else {
        document.location.reload();
      }
    }
    dispatch({ type: 'updateVariantsUsing', useVariants: isChecked });
  };

  const handleSelectVariant = (variantId) => {
    const newArray = selectedVariants.slice();
    const variantIndex = newArray.findIndex((v) => v === variantId);
    if (variantIndex !== -1) {
      newArray.splice(variantIndex, 1);
    } else {
      newArray.push(variantId);
    }
    dispatch({ type: 'setSelectedVariants', selectedVariants: newArray });
  };

  const handleAllSelectVariants = () => {
    if (allSelectedVariants) {
      dispatch({ type: 'setAllSelectedVariants', allSelectedVariants: false });
      dispatch({ type: 'setSelectedVariants', selectedVariants: [] });
    } else {
      dispatch({ type: 'setAllSelectedVariants', allSelectedVariants: true });
      dispatch({
        type: 'setSelectedVariants',
        selectedVariants: variants.map((v) => (v.temporaryId ? v.temporaryId : v.id)),
      });
    }
  };

  const handleAddVariantsForRemoving = (variantObj) => {
    const newObj = Object.assign({}, removingVariants, variantObj);
    dispatch({ type: 'updateRemovingVariants', removingVariants: newObj });
  };

  const handleNeedSaving = () => {
    dispatch({ type: 'setNeedSavingStatus', needSaving: true });
  };

  const handleSetLastAction = (type, data) => {
    dispatch({ type: 'setLastAction', lastAction: { type, data } });
    setUnsavedStatus(true);
  };

  const uploadImages = async (data) => {
    let error = false;
    let id = productId;
    if (!id) {
      await saveProduct().then((product) => {
        id = product.id;
      });
    }
    for (let i = 0; i < data.length; i++) {
      const upfile = data[i];
      await fetchAddImage({
        id,
        upfile,
      }).catch((err) => {
        error = true;
        toast.error(`${t('Ошибка загрузки изображения')} ` + upfile.name);
      });
    }
    if (!error) toast.info(t('Изображения успешно загружены'));
    refreshProduct(id);
  };

  const reorderImages = async (reorderArr) => {
    let error = false;
    for (let i = 0; i < reorderArr.length; i++) {
      const image = reorderArr[i];
      await fetchReorderImage({
        id: image.id,
        pos: i,
      }).catch((err) => {
        error = true;
        toast.error(t('Ошибка обновления позиции изображений'));
      });
    }
    if (!error) toast.info(t('Позиция изображений обновлена'));
    refreshProduct();
  };

  const handleDeleteImage = async (image) => {
    await fetchDeleteImage(image.id)
      .then(() => {
        toast.info(t('Изображение успешно удалено'));
        refreshProduct();
      })
      .catch(() => {
        toast.error(t('Ошибка удаления изображения'));
      });
  };

  const zoomImage = (image) => {
    showModal('ImageEditor', { image, onDelete: handleDeleteImage });
  };

  const handleChangeInput = (id, value) => {
    const oldVal = values[id];

    setFieldValue(id, value);

    handleChange(id, value);

    if (oldVal !== value || id === 'marketplaceCosts') {
      setUnsavedStatus(true);
    }
  };

  const handleChangeWeightType = (value) => {
    if (values.weightType === 'kg' && value === 'gr') {
      setFieldValue('weight', Number(values.weight) * 1000);
    } else if (values.weightType === 'gr' && value === 'kg') {
      setFieldValue('weight', Number(values.weight) / 1000);
    }
    setFieldValue('weightType', value);
  };

  const resetAllInputValuesFromTemplateVariant = (variant) => {
    const emptyDimensions = {
      depth: 0,
      height: 0,
      width: 0,
    };

    if (variant.barcodes) {
      variant.barcodes = variant.barcodes.map((barcode) => {
        return {
          ...barcode,
          value: '',
        };
      });
    }

    variant.dimensions = emptyDimensions;
    variant.dimensionsFact = emptyDimensions;
    variant.marginParams = {
      marketplaceCosts: [],
    };

    if (variant.optionsUsed) {
      variant.optionsUsed = variant.optionsUsed.map((option) => {
        return {
          ...option,
          stringValue: '',
          stringValue: null,
          decimalValue: null,
        };
      });
    }

    variant.ordering = 0;
    variant.price = 0;

    if (variant.prices) {
      variant.prices = variant.prices.map((price) => {
        return {
          ...price,
          value: 0,
        };
      });
    }

    variant.vat = 'NO_VAT';
    variant.weight = 0;
    variant.weightFact = 0;
    variant.article = '';
    variant.extId = '';

    if (variant.eshopSettings) {
      variant.eshopSettings = variant.eshopSettings.map((setting) => {
        return {
          ...setting,
          extId: '',
        };
      });
    }

    return variant;
  };

  const handleAddManualVariant = () => {
    const newVariantData = resetAllInputValuesFromTemplateVariant(variants[0]);
    history.push(getProductEncodedUrl(`${productId}/variants/new`), { newVariantData });
  };

  const openVariant = (variant) => {
    const url = getProductEncodedUrl(productId, variant.id, { archived: isArchived });
    history.push(url);
  };

  const handleCopy = async () => {
    const res = await fetchCopyItem({ id: productId });
    toast.info(t('Товар успешно скопирован'));
    history.push(getProductEncodedUrl(res.id));
  };

  const handleHideShow = () => {
    dispatch({ type: 'setLoading', loading: 'available' });
    const toastMsg = currentProduct.available
      ? t('Товар успешно скрыт')
      : t('Товар успешно отображен');

    fetchHideShowItem(Object.assign({}, currentProduct, { available: !currentProduct.available }))
      .then((product) => {
        toast.info(toastMsg);
        dispatch({ type: 'updateProduct', product });
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        dispatch({ type: 'setLoading', loading: false });
      });
  };

  const openItem = (id) => {
    history.push({
      pathname: `${id}`,
      state: {
        customBackLink: getProductEncodedUrl(currentProduct.id),
      },
    });
    document.location.reload();
  };

  const handleArchiveItem = () => {
    dispatch({ type: 'setLoading', loading: 'archive' });
    const action = currentProduct.deleted ? fetchRestoreItem : fetchArchiveItem;
    const toastMsg = currentProduct.deleted
      ? t('Товар успешно восстановлен')
      : t('Товар успешно добавлен архив');
    const url = getProductEncodedUrl(currentProduct.id, undefined, {
      archived: currentProduct.deleted,
    });

    action(currentProduct)
      .then((product) => {
        toast.info(toastMsg);
        dispatch({ type: 'updateProduct', product });
        dispatch({ type: 'setArchived', isArchived: !isArchived });
        history.push(url);
      })
      .catch((err) => {
        toast.error(parseError(err));
      })
      .finally(() => {
        dispatch({ type: 'setLoading', loading: false });
      });
  };

  const refreshArrearance = async () => {
    const appearanceFields = await fetchGetProductAppearance(productId);
    dispatch({ type: 'setAppearanceFields', appearanceFields });
  };

  const handleExportMovements = async (id) => {
    try {
      const result = await reduxDispatch(fetchExportMovements(id));
      saveAs(result.fileUrl, result.fileID);
    } catch {
      toast.error(t(`Ошибка экспорта`));
    }
  };

  const handleShowStockByBatch = (isExcelExport) => {
    dispatch(
      showModal('StockByBatch', {
        warehouses,
        isExcelExport: isExcelExport,
        stock: values.stock,
        variantId: currentProduct?.variants.map((variant) => variant.id),
      }),
    );
  };

  const handleCreateSkuSet = (type) => {
    reduxDispatch(
      showModal('SkuSetAddingProducts', {
        type: type,
        submitCallback: (data) =>
          handleUpdateSkuSet(
            data.map((p) => ({ id: p.variantId, quantity: p.quantity })),
            null,
            type,
          ).then(() => {
            setFieldValue('type', type);
          }),
      }),
    );
  };

  const handleUpdateSkuSet = (items = [], count, type) => {
    if (isNew) {
      if (unSavedSkuSet && unSavedSkuSet[0].items.length > items.length) {
        return Promise.resolve(
          items.length
            ? dispatch({ type: 'setUnSavedSkuSetSkuSet', unSavedSkuSet: [{ items: items }] })
            : dispatch({ type: 'setUnSavedSkuSetSkuSet', unSavedSkuSet: null }),
        );
      } else {
        const newItems = items.map((item) => {
          const newItemObj = {
            article: item.variants[0].article,
            barcodes: item.variants[0].barcodes,
            id: item.variantId || item.variants[0].id,
            images: item.images,
            optional: false,
            price: item.variants[0].price,
            quantity: item.quantity,
            variantId: item.variantId,
            shortName: item.shortName,
            stockAvailable: item.variants[0].stock.reduce((a, c) => {
              a += c.stockAvailable;
              return a;
            }, 0),
          };

          if (item.items) {
            newItemObj.items = [...item.items];
          }

          return newItemObj;
        });
        return Promise.resolve(
          newItems.length
            ? dispatch({ type: 'setUnSavedSkuSetSkuSet', unSavedSkuSet: [{ items: newItems }] })
            : dispatch({ type: 'setUnSavedSkuSetSkuSet', unSavedSkuSet: null }),
        );
      }
    } else {
      let newItems = null;
      if (unSavedSkuSet) {
        newItems = unSavedSkuSet[0].items.map((item) => {
          return {
            id: item.id,
            quantity: item.quantity,
            optional: item.optional,
          };
        });
        dispatch({ type: 'setUnSavedSkuSetSkuSet', unSavedSkuSet: null });
      }
      return fetchUpdateSKUSet({
        id: currentProduct.variants[0].id,
        skuType: type || values.type,
        bundleOptionalCount: values.type === 'BUNDLE' ? (count ? count : 0) : undefined,
        items: unSavedSkuSet ? newItems.filter((p) => p.quantity) : items.filter((p) => p.quantity),
      })
        .then(() => {
          toast.info(t(`Состав успешно сохранен`));
        })
        .catch((err) => {
          toast.error(parseError(err));
        })
        .finally(() => {
          return actualizeSkuSet();
        });
    }
  };

  const pageTitle = isNew
    ? values.type
      ? itemsTypes[values.type]
      : t('Новый товар')
    : currentProduct
    ? currentProduct.shortName
    : '';

  const pageTitleBadge =
    !isNew && currentProduct && currentProduct.type && currentProduct.type !== 'SKU'
      ? PRODUCT_TYPES.find((t) => t.value === currentProduct.type).label
      : null;
  const breadcrumbs = [{ title: t('Товары'), route: '/items' }, { title: pageTitle }];
  const customBackLink =
    history.location.state && history.location.state.customBackLink
      ? history.location.state.customBackLink
      : null;

  if (!isNew) {
    if (currentProduct) {
      if (!currentProduct.variants || !currentProduct.variants.length) {
        return (
          <ErrorBoundary strict message={t('Возникла ошибка при обработке вариантов товара')} />
        );
      }
    } else {
      return null;
    }
  }

  const isSingleProduct = currentProduct?.variants?.length <= 1;
  const isHaveOptionsUsed = currentProduct?.variants?.[0]?.optionsUsed;
  const haveProductsPriceMarginHistory = productsPriceMarginHistory?.length > 0;

  const openImportChestnyznakModal = () => {
    const data = {
      disableFileTypeToggler: !isSingleProduct,
    };
    if (isSingleProduct) data.id = currentProduct.variants[0].id;
    dispatch(showModal('ImportChestnyznakNumbersModal', data));
  };

  const adaptiveProps = {
    productId,
    pageTitle,
    pageTitleBadge: isMobile().any ? undefined : pageTitleBadge,
    breadcrumbs,
    customBackLink: isMobile().any ? undefined : customBackLink,
    isNew,
    currentProduct,
    values,
    errors,
    handleChangeInput,
    images,
    uploadImages,
    reorderImages,
    zoomImage,
    handleDeleteImage,
    useVariants,
    attributes,
    additionalDescsOptions,
    addNewAttribute,
    handleSubmit,
    warehouses,
    handleChangeWeightType,
    loading,
    isSavedVariants,
    needSaving,
    features,
    variants,
    variantsValidation,
    selectedVariants,
    allSelectedVariants,
    appearanceFields,
    skuSet,
    handleShowStockByBatch,
    handleCreateSkuSet,
    handleUpdateSkuSet,
    handleSelectVariant,
    handleAllSelectVariants,
    options,
    updateVariant,
    deleteVariant,
    deleteLoading,
    addNewFeature,
    dispatch,
    reduxDispatch: isMobile().any ? undefined : reduxDispatch,
    handleVariantsUsing,
    handleNeedSaving,
    handleSetLastAction,
    handleAddManualVariant,
    setUnsavedStatus,
    openVariant,
    refreshArrearance,
    saveProduct,
    isArchived,
    handleCopy,
    allIsValid,
    handleHideShow,
    handleArchiveItem,
    handleExportMovements,
    actualizeSkuSet,
    unsaved: isMobile().any ? unsaved : undefined,
    moveVariant,
    isVirtualSet,
    type,
    unSavedSkuSet,
    openItem,
    eshops,
    productMarginDelivered,
    productMarginShipped,
    isSingleProduct,
    isHaveOptionsUsed,
    productsPriceMarginHistory,
    haveProductsPriceMarginHistory,
    openImportChestnyznakModal,
    refreshProduct,
    monoboxesUpdateRef,
  };

  return (
    <Adaptive
      DekstopComponent={DesktopLayout}
      MobileComponent={MobileLayout}
      adaptiveProps={adaptiveProps}
    />
  );
};

const mapDispatchToProps = {
  fetchGetFeatures,
  fetchGetAttributes,
  fetchGetAdditionalDescs,
  fetchGetItemById,
  fetchAddItem,
  fetchAddFeature,
  fetchAddAttribute,
  fetchAddImage,
  fetchDeleteVariant,
  fetchEditItem,
  fetchReorderImage,
  fetchDeleteImage,
  fetchArchiveItem,
  fetchRestoreItem,
  fetchHideShowItem,
  fetchCopyItem,
  fetchGetProductAppearance,
  fetchGetSKUSets,
  fetchUpdateSKUSet,
  showModal,
  fetchGetEshops,
  fetchGetProductsHistory,
};

export default connect(null, mapDispatchToProps)(SingleItemPage);
